import React, { useState, useEffect } from "react";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Search, SlidersHorizontal } from "lucide-react";
import { Dialog, DialogTrigger } from "../components/ui/dialog";
import AdvancedFilterModal from "../components/AdvancedFilterModal";
import FilterSearchAutocomplete from "../components/FilterSearchAutocomplete";
import { router, usePage } from "@inertiajs/react";

interface FilterBarProps {
  initialFilters?: {
    name?: string;
    city?: string;
    specialties?: string[];
    body_zones?: string[];
    accessibility?: string[];
    equipment?: string[];
    languages?: string[];
    sports?: string[];
    practice_locations?: string[];
    patient_types?: string[];
    payment_methods?: string[];
    techniques?: string[];
    appointment_methods?: string[];
    new_patients_policy?: string;
    waiting_list?: boolean;
    practice_mode?: string;
    parking_type?: string;
  };
}

const FilterBar: React.FC<FilterBarProps> = ({ initialFilters = {} }) => {
  const page = usePage<any>();
  const [location, setLocation] = useState(initialFilters.city || "");
  const [selectedFilters, setSelectedFilters] = useState<Record<string, string[]>>({
    specialties: initialFilters.specialties || [],
    body_zones: initialFilters.body_zones || [],
    accessibility: initialFilters.accessibility || [],
    equipment: initialFilters.equipment || [],
    languages: initialFilters.languages || [],
    sports: initialFilters.sports || [],
    techniques: initialFilters.techniques || [],
    practice_locations: initialFilters.practice_locations || [],
    patient_types: initialFilters.patient_types || [],
    payment_methods: initialFilters.payment_methods || [],
    appointment_methods: initialFilters.appointment_methods || []
  });

  // Mettre à jour les champs si les filtres initiaux changent
  useEffect(() => {
    if (initialFilters.city) setLocation(initialFilters.city);
    
    // Synchroniser tous les filtres avec les filtres initiaux
    setSelectedFilters({
      specialties: initialFilters.specialties || [],
      body_zones: initialFilters.body_zones || [],
      accessibility: initialFilters.accessibility || [],
      equipment: initialFilters.equipment || [],
      languages: initialFilters.languages || [],
      sports: initialFilters.sports || [],
      techniques: initialFilters.techniques || [],
      practice_locations: initialFilters.practice_locations || [],
      patient_types: initialFilters.patient_types || [],
      payment_methods: initialFilters.payment_methods || [],
      appointment_methods: initialFilters.appointment_methods || []
    });
  }, [initialFilters]);

  const categoryMap: Record<string, string> = {
    'SPECIALTIES': 'specialties',
    'BODY_ZONES': 'body_zones',
    'ACCESSIBILITY': 'accessibility',
    'EQUIPMENT': 'equipment',
    'LANGUAGES': 'languages',
    'SPORTS': 'sports',
    'TECHNIQUES': 'techniques',
    'PRACTICE_LOCATIONS': 'practice_locations',
    'PATIENT_TYPES': 'patient_types',
    'PAYMENT_METHODS': 'payment_methods',
    'APPOINTMENT_METHODS': 'appointment_methods'
  };

  // Fonction pour déclencher la recherche
  const performSearch = (filters: Record<string, string[]>, cityFilter: string) => {
    const searchParams: any = {
      city: cityFilter || undefined,
    };

    // Préserver les filtres avancés (modal) depuis les props de la page
    const currentPageFilters = page.props.filters || {};
    const advancedKeys = ['new_patients_policy', 'waiting_list', 'practice_mode', 'parking_type', 'specialist'] as const;
    advancedKeys.forEach(key => {
      const val = currentPageFilters[key];
      if (val !== undefined && val !== null && val !== '' && val !== false) {
        searchParams[key] = typeof val === 'boolean' ? val.toString() : val;
      }
    });

    // Ajouter tous les filtres sélectionnés
    Object.entries(filters).forEach(([key, values]) => {
      if (values.length > 0) {
        searchParams[key] = values;
      }
    });

    // Utiliser Inertia pour la navigation
    router.get('/search', searchParams, {
      preserveState: true,
      preserveScroll: true,
      only: ['physios', 'filters', 'searchMetadata']
    });
  };

  // Gérer la sélection d'un filtre depuis l'autocomplete
  const handleFilterSelect = (suggestion: { id: number; value: string; category_key: string }) => {
    const filterKey = categoryMap[suggestion.category_key];
    if (filterKey) {
      setSelectedFilters(prev => {
        const currentValues = prev[filterKey] || [];
        if (!currentValues.includes(suggestion.value)) {
          const newFilters = {
            ...prev,
            [filterKey]: [...currentValues, suggestion.value]
          };
          // Déclencher la recherche automatiquement
          performSearch(newFilters, location);
          return newFilters;
        }
        return prev;
      });
    }
  };

  // Gérer la suppression d'un filtre
  const handleFilterRemove = (suggestion: { id: number; value: string; category_key: string }) => {
    const filterKey = categoryMap[suggestion.category_key];
    if (filterKey) {
      setSelectedFilters(prev => {
        const currentValues = prev[filterKey] || [];
        const newFilters = {
          ...prev,
          [filterKey]: currentValues.filter(v => v !== suggestion.value)
        };
        // Déclencher la recherche automatiquement après suppression
        performSearch(newFilters, location);
        return newFilters;
      });
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    performSearch(selectedFilters, location);
  };

  // Fonction helper pour créer un hash simple
  const createHash = (str: string): number => {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash;
    }
    return hash;
  };

  // Convertir les filtres pour l'autocomplete
  const selectedFiltersForAutocomplete = Object.entries(selectedFilters).flatMap(([key, values]) => {
    const categoryKey = Object.keys(categoryMap).find(k => categoryMap[k] === key);
    return values.map((value) => ({
      id: createHash(`${categoryKey}-${value}`), // ID unique basé sur la catégorie et la valeur
      value: value,
      category_key: categoryKey || key.toUpperCase()
    }));
  });

  return (
    <div className="sticky top-16 z-40 bg-white border-b border-gray-200 py-3 px-4 md:px-6 flex-shrink-0">
      <form 
        className="flex flex-col md:flex-row gap-3 items-stretch md:items-start max-w-5xl mx-auto"
        onSubmit={handleSubmit}
      >
        <div className="flex-1">
          <FilterSearchAutocomplete
            placeholder="Rechercher une spécialité, zone du corps, équipement..."
            onSelect={handleFilterSelect}
            onRemove={handleFilterRemove}
            selectedFilters={selectedFiltersForAutocomplete}
            className="w-full"
          />
        </div>
        
        <div className="flex-1">
          <div className="h-10">
            <Input
              name="location"
              placeholder="Ville ou code postal"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              className="w-full h-10"
            />
          </div>
        </div>
        
        <Dialog>
          <DialogTrigger asChild>
            <Button 
              type="button" 
              variant="outline" 
              className="md:w-auto w-full h-10 flex items-center justify-center"
            >
              <SlidersHorizontal className="h-4 w-4 mr-2" />
              <span>Filtres</span>
            </Button>
          </DialogTrigger>
          <AdvancedFilterModal />
        </Dialog>
        
        <Button 
          type="submit" 
          variant="default"
          className="md:w-auto w-full h-10"
        >
          <Search className="h-4 w-4 mr-2" />
          <span>Rechercher</span>
        </Button>
      </form>
    </div>
  );
};

export default FilterBar; 
